import { forumId } from '../models/forumMappers';
import { ForumError, forumEnvelope, forumError, invalidResponse, staleResponse } from '../api/forumErrors';

/** One idempotent history write per visible snapshot. Never triggered by fetch. */
export function createForumReadHistory({ http, getIdentity, onSaved = () => {}, onNotificationSaved = () => {} }) {
  const completed = new Map(), pending = new Map(), notices = new Map();
  let scope = null, generation = 0;
  function reset() { generation++; completed.clear(); pending.clear(); notices.clear(); scope = null; }
  function identity() {
    const current = getIdentity() || {}, key = JSON.stringify(current);
    if (scope !== key) { reset(); scope = key; }
    if (!forumId(current.uid)) throw new ForumError('authentication_required', 'Inicia sesión para continuar.', 401);
    return key;
  }
  async function mark(threadId, readAt, notificationCommentId = null) {
    const owner = identity(), epoch = generation, id = forumId(threadId);
    const uid = forumId((getIdentity() || {}).uid), cid = notificationCommentId === null ? null : forumId(notificationCommentId);
    if (!id || !Number.isSafeInteger(readAt) || readAt <= 0 || readAt % 1000 !== 0) throw invalidResponse();
    if (notificationCommentId !== null && !cid) throw invalidResponse();
    const noticeKey = id + ':' + cid;
    if (completed.has(id) && completed.get(id) >= readAt && (!cid || notices.has(noticeKey))) return completed.get(id);
    const key = id + ':' + readAt + ':' + cid;
    if (pending.has(key)) return pending.get(key);
    const current = () => owner === identity() && epoch === generation;
    const request = Promise.resolve().then(async () => {
      if (!current()) throw staleResponse();
      const response = await http.post(`/forum/api/v2/threads/${id}/read`, { read_at: readAt / 1000, ...(cid ? { notification_comment_id: cid } : {}) });
      if (!current()) throw staleResponse();
      const { data } = forumEnvelope(response.data);
      if (forumId(data.thread_id) !== id || !Number.isSafeInteger(data.read_at) || data.read_at < readAt / 1000 || data.read_at > 8640000000000) throw invalidResponse();
      if (cid && (!data.notification || forumId(data.notification.comment_id) !== cid || ![data.notification.total, data.notification.unread].every(n => Number.isSafeInteger(n) && n >= 0) || data.notification.unread > data.notification.total)) throw invalidResponse();
      const saved = Math.max(data.read_at * 1000, completed.get(id) || 0);
      completed.delete(id); completed.set(id, saved);
      while (completed.size > 20) completed.delete(completed.keys().next().value);
      if (cid) {
        notices.delete(noticeKey); notices.set(noticeKey, true);
        while (notices.size > 20) notices.delete(notices.keys().next().value);
        onNotificationSaved({ uid, commentId: cid, total: data.notification.total, unread: data.notification.unread });
      }
      onSaved(id, saved);
      return saved;
    }).catch(error => { if (!current()) throw staleResponse(); throw forumError(error); })
      .finally(() => { if (pending.get(key) === request) pending.delete(key); });
    pending.set(key, request);
    return request;
  }
  return { mark, reset };
}

/** View-local observation; no global scroll locks or polling interval. */
export function observeForumReading(element, callback, { documentRef = document, windowRef = window } = {}) {
  let stopped = false, intersects = false;
  function visible() {
    if (stopped || documentRef.visibilityState !== 'visible' || !element.isConnected) return;
    if (observer && !intersects) return;
    const rect = element.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0 && rect.bottom > 62 && rect.top < windowRef.innerHeight && rect.right > 0 && rect.left < windowRef.innerWidth) callback();
  }
  const observer = windowRef.IntersectionObserver ? new windowRef.IntersectionObserver(entries => {
    intersects = entries.some(entry => entry.target === element && entry.isIntersecting);
    visible();
  }, { rootMargin: '-62px 0px 0px 0px' }) : null;
  if (observer) observer.observe(element);
  else windowRef.addEventListener('scroll', visible, true);
  documentRef.addEventListener('visibilitychange', visible);
  windowRef.addEventListener('resize', visible);
  visible();
  return () => {
    stopped = true;
    if (observer) observer.disconnect();
    else windowRef.removeEventListener('scroll', visible, true);
    documentRef.removeEventListener('visibilitychange', visible);
    windowRef.removeEventListener('resize', visible);
  };
}
